import { Route } from '@angular/router';

import { CalendarResolver } from '@finder/shared/resolvers/calendar.resolver';
import { DynamicResolver } from '@finder/shared/resolvers/dynamic.resolver';
import { DynamicRouteMatcher } from '@finder/shared/route-matchers/dynamic-route.matcher';
import { ListResolver } from '@finder/shared/resolvers/list.resolver';
import { ListRouteMatcher } from '@finder/shared/route-matchers/list-route.matcher';
import { MyIdGuard } from '@finder/shared/guards/myId/myId.guard';
import { SearchResolver } from '@finder/shared/resolvers/search.resolver';
import { CALENDAR_ROUTES } from '@finder/features/calendars/calendar.routes';
import { LIST_ROUTES } from '@finder/features/list/list.routes';
import { SEARCH_ROUTES } from '@finder/features/search/search.routes';
import { DEBUG_ROUTES } from '@finder/features/debug/debug.routes';
import { THEME_ENGINE_ROUTES } from '@finder/features/theme-engine/theme-engine.routes';
import { EveResolver } from '@finder/shared/resolvers/eve.resolver';
import { EVE_ROUTES } from '@finder/features/eve/eve.routes';
import { AiSearchResolver } from '@finder/features/ai-search/resolvers/ai-search.resolver';
import { AI_SEARCH } from '@finder/features/ai-search/ai-search.routes';
import { DEMO_ROUTES } from '@finder/features/demo/demo.routes';
import { FinderAnalyticsService } from '@finder/shared/services/finder-analytics/finder-analytics.service';
import { TravelAgentService } from '@finder/shared/services/travel-agent/travel-agent.service';
import { InterstitialService } from '@finder/shared/services/interstitial-service/interstitial-service.service';
import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { MyPlansService } from '@finder/shared/services/my-plans-service/my-plans.service';
import { ScheduleService } from '@finder/shared/services/schedule/schedule.service';
import { SortService } from '@finder/features/list/services/sort/sort.service';
import { DiningReservationsConflictModalService } from '@finder/features/list/services/dining-reservations-conflict-modal/dining-reservations-conflict-modal.service';
import { DebugPanelService } from '@finder/shared/services/debug-panel/debug-panel.service';
import { FilterEmitterService } from '@finder/shared/services/filter-emitter-service/filter-emitter.service';
import { HeightSliderService } from '@finder/features/list/components/filters/components/height-slider/height-slider.service';
import { DiningListReservationsService } from '@finder/features/list/services/dining-list-reservations/dining-list-reservations.service';
import { FiltersHashService } from '@finder/features/list/services/filters-hash/filters-hash.service';
import { ListService } from '@finder/features/list/services/list/list.service';
import { ModalService } from '@finder/shared/components/modal/modal.service';
import { MultiSelectService } from '@finder/shared/services/multi-select/multi-select.service';
import { WebSocketsService } from '@finder/shared/services/websockets/websockets.service';
import { CalendarService } from '@finder/shared/services/calendar-service/calendar-service.service';
import { GetSimpleDatePipe } from '@finder/shared/components/calendar/get-simple-date.pipe';
import { ParallaxBackgroundService } from '@finder/shared/components/parallax-background/parallax-background.service';
import { AnimationService } from '@finder/shared/components/animations/animation-service/animation.service';
import { CalendarsService } from '@finder/features/calendars/services/calendars.service';
import { ThemeEngineService } from '@finder/features/theme-engine/services/theme-engine.service';
import { ContentInspectionConnectorService } from '@finder/shared/services/content-inspection-connector/content-inspection-connector.service';
import { AiSearchService } from '@finder/features/ai-search/services/ai-search/ai-search.service';

/**
 * Another way to load the child routes
 * https://v17.angular.io/guide/standalone-components#lazy-loading-many-routes-at-once
 * https://stackoverflow.com/questions/77231371/how-to-register-nested-routes-on-an-angular-standalone-component
 *
 * This may help resolve the circular dependency for the Demo pages
 * https://v17.angular.io/guide/standalone-components#resolve-circular-dependencies-with-a-forward-class-reference
 *
 * Routes might not be resolved at this point. We might need to leave the `resolve` property in the child routes
 *
 * Service notes
 *
 * ScheduleService: We have to provide this at an route level because providing
 * it at the detail level is not enough. If we provide it there and use provideIn: root
 * other services will spin up a new instance of this Singleton and things will not
 * work.
 *
 * ** SSR Notes **
 * Anything with `advanced-finder` in the path will be skipped for SSR. We don't need to render that
 * server side.
 */
const calendarProviders = [
    CalendarResolver,
    CalendarsService,
    CalendarService,
    GetSimpleDatePipe,
];
const searchProviders = [
    SearchResolver,
];
const aiSearchProviders = [
    AiSearchResolver,
    AiSearchService,
];
export const APPLICATION_ROUTES: Route[] = [
    {
        path: 'advanced-finder/auth',
        loadComponent: () => import('@finder/shared/components/auth/auth.component').then(m => m.AuthCallbackComponent),
    },
    {
        path: 'advanced-finder/demo',
        loadComponent: () => import('@finder/features/demo/demo.component').then(m => m.DemoComponent),
        canActivate: [MyIdGuard],
        children: DEMO_ROUTES,
    },
    {
        path: ':locale/advanced-finder/debug',
        loadComponent: () => import('@finder/features/debug/debug.component').then(m => m.DebugComponent),
        canActivate: [MyIdGuard],
        children: DEBUG_ROUTES
    },
    {
        path: 'advanced-finder/debug',
        loadComponent: () => import('@finder/features/debug/debug.component').then(m => m.DebugComponent),
        canActivate: [MyIdGuard],
        children: DEBUG_ROUTES
    },
    {
        path: 'advanced-finder/eve',
        loadComponent: () => import('@finder/features/eve/eve.component').then(m => m.EveComponent),
        canActivate: [MyIdGuard],
        resolve: { eve: EveResolver },
        children: EVE_ROUTES,
        providers: [
            EveResolver,
            ContentInspectionConnectorService,
        ]
    },
    {
        path: 'advanced-finder/theme-engine',
        loadComponent: () => import('@finder/features/theme-engine/theme-engine.component').then(m => m.ThemeEngineComponent),
        canActivate: [MyIdGuard],
        children: THEME_ENGINE_ROUTES,
        providers: [
            ThemeEngineService,
        ]
    },
    {
        path: ':locale/ai-search',
        loadComponent: () => import('@finder/features/ai-search/ai-search.component').then(m => m.AiSearchComponent),
        canActivate: [MyIdGuard],
        pathMatch: 'full',
        resolve: { search: AiSearchResolver },
        children: AI_SEARCH,
        providers: aiSearchProviders,
    },
    {
        path: 'ai-search',
        loadComponent: () => import('@finder/features/ai-search/ai-search.component').then(m => m.AiSearchComponent),
        canActivate: [MyIdGuard],
        pathMatch: 'full',
        resolve: { search: AiSearchResolver },
        children: AI_SEARCH,
        providers: aiSearchProviders,
    },
    {
        path: ':locale/search',
        loadComponent: () => import('@finder/features/search/search.component').then(m => m.SearchComponent),
        pathMatch: 'full',
        data: {
            pageType: 'search'
        },
        resolve: { search: SearchResolver },
        children: SEARCH_ROUTES,
        providers: searchProviders,
    },
    {
        path: 'search',
        loadComponent: () => import('@finder/features/search/search.component').then(m => m.SearchComponent),
        pathMatch: 'full',
        data: {
            pageType: 'search'
        },
        resolve: { search: SearchResolver },
        children: SEARCH_ROUTES,
        providers: searchProviders,
    },
    {
        path: ':locale/calendars',
        loadComponent: () => import('@finder/features/calendars/calendars.component').then(m => m.CalendarsComponent),
        data: {
            pageType: 'calendars'
        },
        children: CALENDAR_ROUTES,
        resolve: { calendar: CalendarResolver },
        providers: calendarProviders
    },
    {
        path: 'calendars',
        loadComponent: () => import('@finder/features/calendars/calendars.component').then(m => m.CalendarsComponent),
        data: {
            pageType: 'calendars'
        },
        children: CALENDAR_ROUTES,
        resolve: { calendar: CalendarResolver },
        providers: calendarProviders
    },
    {
        path: ':reservations/:store/calendars',
        loadComponent: () => import('@finder/features/calendars/calendars.component').then(m => m.CalendarsComponent),
        data: {
            pageType: 'calendars'
        },
        children: CALENDAR_ROUTES,
        resolve: { calendar: CalendarResolver },
        providers: calendarProviders
    },
    {
        path: 'park-calendars',
        loadComponent: () => import('@finder/features/calendars/calendars.component').then(m => m.CalendarsComponent),
        data: {
            pageType: 'calendars'
        },
        children: CALENDAR_ROUTES,
        resolve: { calendar: CalendarResolver },
        providers: calendarProviders
    },
    {
        path: ':locale/park-calendars',
        loadComponent: () => import('@finder/features/calendars/calendars.component').then(m => m.CalendarsComponent),
        data: {
            pageType: 'calendars'
        },
        children: CALENDAR_ROUTES,
        resolve: { calendar: CalendarResolver },
        providers: calendarProviders
    },
    {
        matcher: ListRouteMatcher, // only list and detail/campaign should use the matcher
        loadComponent: () => import('@finder/features/list/list.component').then(m => m.ListComponent),
        pathMatch: 'full',
        data: {
            pageType: 'list'
        },
        children: LIST_ROUTES,
        resolve: { list: ListResolver },
        providers: [
            ListResolver,  // Provide the resolver at the top
            TravelAgentService,
            SortService,
            ViewportDetectionService,
            DebugPanelService,
            DiningReservationsConflictModalService,
            FilterEmitterService,
            FiltersHashService,
            ListService,
            DiningListReservationsService,
            MultiSelectService,
            WebSocketsService,
            HeightSliderService,
            ModalService,
        ]
    },
    {
        matcher: DynamicRouteMatcher, // only list and detail/campaign should use the matcher
        loadComponent: () => import('@finder/features/dynamic-loader/dynamic-loader.component').then(m => m.DynamicLoaderComponent),
        data: {
            pageType: 'dynamic'
        },
        resolve: { svc: DynamicResolver },
        // These services aren't injected with 'root' so we have to manually add them at the component start
        providers: [
            DynamicResolver, // Provide the resolver at the top
            TravelAgentService,
            CalendarService,
            ParallaxBackgroundService,
            AnimationService,
            InterstitialService,
            ViewportDetectionService,
            DebugPanelService,
            MyPlansService,
            ScheduleService,
            WebSocketsService,
            GetSimpleDatePipe,
        ]
    }
];
